import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  SelectOption,
  UserPermission,
  useNotify
} from '@globaleris/ui.library'
import { Box, Breadcrumbs, Button, Card, CardContent, Typography } from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { ChildSettlementGroupRequestModelDTO } from '../../../api/Argentia/endpoints/settlementGroups/model/settlementGroups'
import { EnumTypeSettlementGroup } from '../../../api/Argentia/enums/typeSettlementGroup'
import Page from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { BreadcrumbLink } from '../../../components/shared/breadcrumbLink/breadcrumbLink'
import { CardEmptyContent } from '../../../components/shared/cardEmptyContent/cardEmptyContent'
import { LoadingCard } from '../../../components/shared/loadingCard/loadingCard'
import { enumToSelectOptionArray } from '../../../helpers'
import { getUserPermissions, hasPermission } from '../../../helpers/permissionsHelper'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useConfirmationDialog, useFormDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { Pages, Resources } from '../../../model/RBAC/constants'
import { getCustomerDomain } from '../../../redux'
import {
  mapChildSettlementGroupFormDataToRequest,
  mapChildSettlementGroupTableData
} from '../settlementGroups.mapper'
import {
  ChildSettlementGroupTableData,
  FormAddChildSettlementGroup
} from '../settlementGroups.model'
import {
  createChildSettlementGroup,
  deleteChildSettlementGroup,
  getChildSettlementGroups,
  getParentSettlementGroupOptions,
  updateChildSettlementGroup
} from '../settlementGroups.service'

type ChildSettlementGroupPageProps = {
  location: {
    state: {
      settlementGroupId: number
      settlementGroupCode: string
      settlementGroupDesc: string
    }
  }
}

export const ChildSettlementGroupPage: React.FC<ChildSettlementGroupPageProps> = props => {
  // Custom Hooks
  const [t] = useTranslation(['globalerisLibrary', 'settlementGroups', 'enums'])
  const history = useHistory()
  const { openForm, updateFormDefinition, updateFormData, closeForm, formProps, CustomFormDialog } =
    useFormDialog()
  const { info, error } = useNotify()

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  // TableData
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [childSettlementGroupData, setChildSettlementGroupData] = useState<
    ChildSettlementGroupTableData[] | undefined
  >(undefined)
  const [noData, setNoData] = useState<boolean>(false)

  // FormInput Options
  const [isLoadingParentOptions, setIsLoadingParentOptions] = useState<boolean>(false)
  const [parentGroupOptions, setParentGroupOptions] = useState<SelectOption[]>([])
  const [formMode, setFormMode] = useState<FormDialogMode | null>(null)

  // Extracting data from route state
  const groupId = props.location.state.settlementGroupId
  const groupCode = props.location.state.settlementGroupCode
  const groupDesc = props.location.state.settlementGroupDesc
  const groupBreadcrumb = `${groupCode} - ${groupDesc}`

  //form Validations
  const [invalidDates, setInvalidDates] = useState<boolean>(false)
  const [overlappedDates, setOverlappedDates] = useState<boolean>(false)

  // Handlers Starts
  const handleClickBackButton = () => {
    history.goBack()
  }

  const handleAddDialogOpen = async () => {
    const mode = FormDialogMode.ADD

    setFormMode(FormDialogMode.ADD)

    const def = buildFormDefinition(mode)

    openForm({
      definition: def,
      title: t('settlementGroups:components.childSettlementGroups.forms.add.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: (formData: any) => onCloseHandler(formData, mode)
    })

    return true
  }

  const handleEditDialogOpen = async selectedRow => {
    const mode = FormDialogMode.EDIT

    setFormMode(FormDialogMode.EDIT)
    const def = buildFormDefinition(mode)
    openForm({
      definition: def,
      title: t('settlementGroups:components.childSettlementGroups.forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: {
        vinculationStartDate: selectedRow.startDate,
        vinculationEndDate: selectedRow.endDate,
        childSettlementGroupId: selectedRow.childSettlementGroupId
      },
      onClose: (formData: any) => onCloseHandler(formData, mode)
    })

    return true
  }

  const onCloseHandler = async (formData: FormAddChildSettlementGroup, mode: FormDialogMode) => {
    if (formData) {
      const isEditMode = mode === FormDialogMode.EDIT

      if (missingFieldsFormWithRequiredText(formDefinition.sections!, formData)) {
        error(t('globalerisLibrary:formDialog.validations.missingRequiredFields'))
        return
      }

      // Map data from CustomFormDialog (Modal)
      const request = mapChildSettlementGroupFormDataToRequest(formData, groupId)

      try {
        if (isEditMode) {
          await updateChildGroup(request)
        } else {
          await createChildGroup(request)
        }
      } catch (error) {
        return
      }
    }
    closeForm()
    // clean form dialog
    setFormMode(null)
    setInvalidDates(false)
    setOverlappedDates(false)
  }

  const handleDeleteDialogOpen = (selectedRow: ChildSettlementGroupTableData) => {
    getConfirmation(ConfirmationDialogs.DELETE_SETTLEMENTGROUP_CHILD).then(async () => {
      await deleteChildGroup(groupId, selectedRow.childSettlementGroupId).then(response => {})
      closeConfirmation()
    })
  }

  // Handlers Ends

  // Functions Starts

  const fetchChildSettlementGroups = useCallback(async (parentId: number) => {
    setIsFetching(true)
    await getChildSettlementGroups(parentId).then(response => {
      if (response.success) {
        if (response.data) {
          setNoData(response.data.length === 0)
          setChildSettlementGroupData(response.data)
        }
      }
      setIsFetching(false)
    })
  }, [])

  const createChildGroup = useCallback(async (request: ChildSettlementGroupRequestModelDTO) => {
    await createChildSettlementGroup(request).then(response => {
      if (response.success) {
        if (response.data) {
          info(t('settlementGroups:components.childSettlementGroups.forms.add.notifications.save'))
          fetchChildSettlementGroups(request.parentSettlementGroupId)
        }
      } else {
        throw new Error()
      }
    })
  }, [])

  const updateChildGroup = useCallback(async (request: ChildSettlementGroupRequestModelDTO) => {
    await updateChildSettlementGroup(request).then(response => {
      if (response.success) {
        if (response.data) {
          info(t('settlementGroups:components.childSettlementGroups.forms.edit.update.success'))
          fetchChildSettlementGroups(request.parentSettlementGroupId)
        }
      }
    })
  }, [])

  const deleteChildGroup = useCallback(async (parentId: number, childId: number) => {
    await deleteChildSettlementGroup(parentId, childId).then(response => {
      if (response.success) {
        info(t('settlementGroups:notifications.delete.success'))
        fetchChildSettlementGroups(parentId)
      }
    })
  }, [])

  const getAvailableParentGroupOptions = useCallback(
    (
      parentOptions: SelectOption[],
      currentGroupId: number,
      existingChildGroups?: ChildSettlementGroupTableData[]
    ): SelectOption[] => {
      return parentOptions.filter(sg => {
        const optionId = +sg.value
        const isCurrentGroup = optionId === currentGroupId
        const isAlreadyChild = existingChildGroups?.some(
          child => child.childSettlementGroupId === optionId
        )

        return !isCurrentGroup && !isAlreadyChild
      })
    },
    []
  )

  const validateDates = useCallback(
    (formData: FormAddChildSettlementGroup, excludeChildId?: number): boolean => {
      let invalidDates: boolean = false
      let overlappedDates: boolean = false
      if (!formData) return true

      // Check if start date is before end date
      if (formData.vinculationEndDate) {
        invalidDates = moment(formData.vinculationEndDate).isBefore(
          formData.vinculationStartDate!,
          'day'
        )
      }

      // Check if dates are overlapped with existing child settlement groups
      // this logic is commented out for now as it may apply to father settlement groups in the future only

      // if (childSettlementGroupData && childSettlementGroupData.length > 0) {
      //   const startDate = moment(formData.vinculationStartDate!)
      //   const endDate = formData.vinculationEndDate ? moment(formData.vinculationEndDate) : null

      //   overlappedDates = childSettlementGroupData
      //     .filter(item => {
      //       // Excluir el registro actual durante la edición
      //       return excludeChildId ? item.childSettlementGroupId !== excludeChildId : true
      //     })
      //     .some(item => {
      //       const itemStartDate = moment(item.startDate)
      //       const itemEndDate = moment(item.endDate)

      //       return (
      //         startDate.isBetween(itemStartDate, itemEndDate, 'day', '[]') ||
      //         (endDate && endDate.isBetween(itemStartDate, itemEndDate, 'day', '[]'))
      //       )
      //     })
      // }

      setInvalidDates(invalidDates)
      setOverlappedDates(overlappedDates)

      return invalidDates || overlappedDates
    },
    [childSettlementGroupData]
  )

  // Functions Ends

  // Form Definition

  const buildFormDefinition = (mode: FormDialogMode | null): FormDialogDefinitionProps => {
    const isEditMode = mode === FormDialogMode.EDIT

    return {
      id: isEditMode ? 'EDIT' : 'ADD',
      sections: [
        {
          inputs: [
            {
              name: 'childSettlementGroupId',
              label: t(
                'settlementGroups:components.childSettlementGroups.forms.add.inputs.settlementGroup'
              ),
              type: DataType.SELECT,
              isLoading: !isEditMode ? isLoadingParentOptions : undefined,
              selectOptions: isEditMode
                ? parentGroupOptions
                : getAvailableParentGroupOptions(
                    parentGroupOptions,
                    groupId,
                    childSettlementGroupData
                  ),
              readOnly: isEditMode,
              required: true,
              inputSize: 12
            },
            {
              name: 'vinculationStartDate',
              label: t(
                'settlementGroups:components.childSettlementGroups.forms.add.inputs.vinculationStartDate'
              ),
              type: DataType.DATE,
              required: true,
              onChange: (value: number | string, formState) => {
                const formData = formState as FormAddChildSettlementGroup
                // Pasar el ID del registro actual si estamos en modo edición
                const excludeId = isEditMode ? formData?.childSettlementGroupId : undefined
                validateDates(formData, excludeId)
                updateFormData({
                  ...formState,
                  vinculationStartDate: value.toString()
                })
              },
              errorState: invalidDates || overlappedDates,
              errorText: invalidDates
                ? t(
                    'settlementGroups:components.childSettlementGroups.forms.validations.endDateBeforeStartDate'
                  )
                : overlappedDates
                  ? t(
                      'settlementGroups:components.childSettlementGroups.forms.validations.overlappedDates'
                    )
                  : ''
            },
            {
              name: 'vinculationEndDate',
              label: t(
                'settlementGroups:components.childSettlementGroups.forms.add.inputs.vinculationEndDate'
              ),
              type: DataType.DATE,
              clearable: true,
              onChange: (value: number | string, formState) => {
                const formData = formState as FormAddChildSettlementGroup
                // Pasar el ID del registro actual si estamos en modo edición
                const excludeId = isEditMode ? formData.childSettlementGroupId : undefined
                validateDates(formData, excludeId)
                updateFormData({
                  ...formState,
                  vinculationEndDate: value.toString()
                })
              },
              errorState: invalidDates || overlappedDates,
              errorText: invalidDates
                ? t(
                    'settlementGroups:components.childSettlementGroups.forms.validations.endDateBeforeStartDate'
                  )
                : overlappedDates
                  ? t(
                      'settlementGroups:components.childSettlementGroups.forms.validations.overlappedDates'
                    )
                  : ''
            }
          ]
        }
      ]
    }
  }

  const formDefinition = React.useMemo(
    () => buildFormDefinition(formMode),
    [
      formMode,
      isLoadingParentOptions,
      parentGroupOptions,
      groupId,
      childSettlementGroupData,
      getAvailableParentGroupOptions,
      invalidDates,
      overlappedDates,
      validateDates
    ]
  )

  // DataTable Config Starts

  const dialogTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      type: DataType.NUMBER,
      display: false
    },
    {
      name: 'childGroupCode',
      label: t('settlementGroups:components.childSettlementGroups.dataTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'childGroupDescription',
      label: t('settlementGroups:components.childSettlementGroups.dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'childType',
      label: t(
        'settlementGroups:components.childSettlementGroups.dataTable.columns.typeSettlementGroup'
      ),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumTypeSettlementGroup,
        'enums:EnumTypeSettlementGroup'
      )
    },
    {
      name: 'startDate',
      label: t('settlementGroups:components.childSettlementGroups.dataTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('settlementGroups:components.childSettlementGroups.dataTable.columns.endDate'),
      type: DataType.DATE
    }
  ]

  let dialogTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    analytics: {
      customer: getCustomerDomain(),
      view: t('settlementGroups:components.childSettlementGroups.dataTable.analytics')
    },
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    canEdit: false,
    canDelete: false,
    customInsertEditModal: hasPermission(Resources.ExtendedClients, [UserPermission.EDIT]),
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: undefined,
    onRowInsert: hasPermission(Resources.ExtendedClients, [UserPermission.EDIT])
      ? handleAddDialogOpen
      : undefined,
    rowCustomActions: [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t(
          'settlementGroups:components.childSettlementGroups.dataTable.rowCustomActions.edit'
        ),
        onClick: handleEditDialogOpen,
        userPermission: UserPermission.EDIT
      },
      {
        icon: <Delete fontSize="small" />,
        tooltip: t(
          'settlementGroups:components.childSettlementGroups.dataTable.rowCustomActions.delete'
        ),
        onClick: handleDeleteDialogOpen,
        userPermission: UserPermission.EDIT
      }
    ]
  }

  // DataTable Config Ends

  // Hooks Starts

  // useEffect exclusive for sideEffects
  useEffect(() => {
    const fetchParentSettlementGroupOptions = async () => {
      setIsLoadingParentOptions(true)
      await getParentSettlementGroupOptions()
        .then(response => {
          if (response.success && response.data && response.data.length > 0) {
            setParentGroupOptions(response.data)
          }
        })
        .finally(() => {
          setIsLoadingParentOptions(false)
        })
    }

    fetchParentSettlementGroupOptions()
  }, [])

  useEffect(() => {
    fetchChildSettlementGroups(groupId)
  }, [groupId, fetchChildSettlementGroups])

  // useEffect for updating Modal Form
  useEffect(() => {
    updateFormDefinition(formDefinition)
  }, [formDefinition])

  // Hooks Ends

  if (!groupCode) {
    history.push(NonAuthRoutes.SettlementGroups)
  }

  return (
    <Page fullWidth={true}>
      <PageHeader
        title={t('settlementGroups:components.childSettlementGroups.title', { groupCode })}
      >
        <Button variant="text" color="primary" onClick={handleClickBackButton}>
          {t('settlementGroups:actions.back')}
        </Button>
      </PageHeader>
      <PageCenterColumn>
        <Box mb={1}>
          <Breadcrumbs separator=">" aria-label="breadcrumb">
            <BreadcrumbLink toPathName="/settlementGroups">
              {`${t('settlementGroups:breadcrumbs.settlementGroups')}`}
            </BreadcrumbLink>
            <Typography key="3" color="primary" variant="subtitle2">
              {t('settlementGroups:breadcrumbs.group', { groupBreadcrumb })}
            </Typography>
            <Typography key="4" color="primary" variant="subtitle2">
              {t('settlementGroups:breadcrumbs.childSettlementGroups')}
            </Typography>
          </Breadcrumbs>
        </Box>
        {isFetching ? (
          <LoadingCard />
        ) : noData ? (
          <CardEmptyContent
            title={t('settlementGroups:components.childSettlementGroups.emptyCard.title')}
            caption={t('settlementGroups:components.childSettlementGroups.emptyCard.caption')}
          >
            <Button size="small" color="primary" variant="contained" onClick={handleAddDialogOpen}>
              {t('settlementGroups:components.childSettlementGroups.emptyCard.buttonLabel')}
            </Button>
          </CardEmptyContent>
        ) : (
          <Card raised>
            <CardContent>
              <DataTable
                title={t('settlementGroups:components.childSettlementGroups.dataTable.title')}
                data={
                  childSettlementGroupData &&
                  mapChildSettlementGroupTableData(childSettlementGroupData)
                }
                columns={dialogTableColumns}
                options={dialogTableOptions}
                userPermissions={getUserPermissions(Pages.SettlementGroupChild)}
              />
            </CardContent>
          </Card>
        )}
        <CustomFormDialog {...formProps} />
      </PageCenterColumn>
    </Page>
  )
}
